import React from 'react';
import { Alert, Button, Box, Typography } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { AuthUser } from '../../types/common';

interface DeletionWarningBannerProps {
  user: AuthUser | null;
  onCancelDeletion: () => void;
  loading?: boolean;
}

/**
 * Warning banner displayed when user has a pending deletion request
 * Shows at the top of the application for logged-in users
 */
export const DeletionWarningBanner: React.FC<DeletionWarningBannerProps> = ({
  user,
  onCancelDeletion,
  loading = false,
}) => {
  const { t } = useTranslation();

  // Helper function to calculate remaining days
  const calculateRemainingDays = (deletedAt: string | null | undefined): number => {
    if (!deletedAt) {return 0;}
    const deletionDate = new Date(deletedAt);
    const expiryDate = new Date(deletionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
  };

  // Don't show banner if user is null or no deletion is scheduled
  if (!user?.deleted_at) {
    return null;
  }

  const daysRemaining = calculateRemainingDays(user.deleted_at);

  // Don't show if grace period has expired
  if (daysRemaining <= 0) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      icon={<WarningIcon />}
      sx={{
        borderRadius: 0,
        borderBottom: '1px solid',
        borderBottomColor: 'warning.main',
        backgroundColor: 'warning.light',
        '& .MuiAlert-icon': {
          alignItems: 'center',
        },
      }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={onCancelDeletion}
          disabled={loading}
          sx={{
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {t('users.deletionBanner.action')}
        </Button>
      }
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('users.deletionBanner.title')}
        </Typography>
        <Typography variant="body2">
          {t('users.deletionBanner.message', { days: t('users.daysRemaining', { count: daysRemaining }) })}
        </Typography>
      </Box>
    </Alert>
  );
};
